import {
  AnimaliaListingMapper,
  AnimaliaListingPageDTO,
  AnimaliaListingActiveFilterDTO,
} from '../mappers/AnimaliaListingMapper';
import {
  AnimaliaListFilters,
  AnimaliaListRepositoryPort,
} from '../../domain/ports/AnimaliaListRepositoryPort';
import { AnimaliaListItem } from '../../domain/entities/AnimaliaListItem';
import { AnimaliaListingTaxonomy } from '../../domain/entities/AnimaliaListingTaxonomy';
import {
  serializeAnimaliaListFilters,
  normalizeAnimaliaListFilters,
  toAnimaliaNavigableFilters,
} from '../taxonomy/AnimaliaNavigableTaxonomy';

const LIST_BATCH_LIMIT = 20;

interface FetchAnimaliaListingInput {
  readonly filters?: AnimaliaListFilters;
  readonly reset?: boolean;
}

export class FetchAnimaliaListingUseCase {
  private readonly executedIds = new Set<string>();

  private loadedItems: AnimaliaListItem[] = [];

  private activeFilters: AnimaliaListFilters | undefined;

  private activeFiltersSignature = '';

  private activeTaxonomy: AnimaliaListingTaxonomy | null = null;

  constructor(private readonly repository: AnimaliaListRepositoryPort) {}

  async execute(input?: FetchAnimaliaListingInput): Promise<AnimaliaListingPageDTO> {
    const normalizedFilters = normalizeAnimaliaListFilters(input?.filters);
    const nextFiltersSignature = serializeAnimaliaListFilters(normalizedFilters);
    const shouldResetSession = input?.reset || nextFiltersSignature !== this.activeFiltersSignature;

    if (shouldResetSession) {
      this.resetSession();
      this.activeFilters = normalizedFilters;
      this.activeFiltersSignature = nextFiltersSignature;
    }

    const nextBatch = await this.repository.fetchList(this.activeFilters, [...this.executedIds]);
    const unseenItems = nextBatch.items.filter((item) => !this.executedIds.has(item.id));

    unseenItems.forEach((item) => {
      this.executedIds.add(item.id);
    });

    this.loadedItems = [...this.loadedItems, ...unseenItems];
    this.activeTaxonomy = nextBatch.taxonomy ?? this.activeTaxonomy;

    return AnimaliaListingMapper.toPageDTO(
      this.loadedItems,
      nextBatch.items.length === LIST_BATCH_LIMIT,
      this.getActiveFiltersDTO(),
      this.activeTaxonomy,
    );
  }

  resetSession(): void {
    this.executedIds.clear();
    this.loadedItems = [];
    this.activeTaxonomy = null;
  }

  private getActiveFiltersDTO(): ReadonlyArray<AnimaliaListingActiveFilterDTO> {
    return toAnimaliaNavigableFilters(this.activeFilters);
  }
}
